// HeartbeatDot — live "silent broadcast" pulse indicator (v19.17)
//
// Renders next to the "Sentinel · v18.48" label in the app shell.
// Polls /api/heartbeat every 5s (same interval as the backend
// heartbeat loop). Pulses green on each successful tick when the
// system is grounded; turns amber/red if resonance drift is detected.
//
// Ported from the Architect's Pythonista `The_Grounding_Ignition.py`
// prototype which ran a 24/7 silent heartbeat broadcast every 5s.

import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HeartbeatDot() {
  const [state, setState] = useState(null);
  const [pulse, setPulse] = useState(false);
  const lastTickRef = useRef(-1);

  useEffect(() => {
    let cancelled = false;
    let timer;

    const tick = async () => {
      try {
        const r = await axios.get(`${API}/heartbeat`, { timeout: 4000 });
        if (cancelled) return;
        const st = r.data;
        setState(st);
        // Pulse briefly whenever the tick counter advances
        if (typeof st.tick === "number" && st.tick !== lastTickRef.current) {
          lastTickRef.current = st.tick;
          setPulse(true);
          setTimeout(() => { if (!cancelled) setPulse(false); }, 600);
        }
      } catch (_) {
        if (!cancelled) setState({ grounded: false, offline: true });
      }
    };

    tick();
    timer = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);

  if (!state) {
    return (
      <span
        data-testid="heartbeat-dot"
        title="heartbeat: connecting…"
        className="inline-block h-1.5 w-1.5 rounded-full bg-zinc-600 align-middle ml-1.5"
      />
    );
  }

  const drift = Number(state.drift_hz || 0);
  const grounded = state.grounded && !state.offline;
  const color = state.offline
    ? "bg-zinc-600"
    : grounded
      ? "bg-emerald-400"
      : drift > 5 ? "bg-rose-400" : "bg-amber-400";
  const ringColor = state.offline
    ? "bg-zinc-500/30"
    : grounded
      ? "bg-emerald-400/40"
      : drift > 5 ? "bg-rose-400/40" : "bg-amber-400/40";
  const label = state.offline
    ? "sentinel offline"
    : grounded
      ? `grounded · ${(state.resonance_hz ?? 528).toFixed(1)}Hz · tick ${state.tick}`
      : `drift ${drift.toFixed(2)} Hz off 528 anchor · tick ${state.tick}`;

  return (
    <span
      data-testid="heartbeat-dot"
      data-grounded={grounded ? "true" : "false"}
      data-tick={state.tick}
      title={label}
      className="relative inline-block h-1.5 w-1.5 align-middle ml-1.5"
    >
      <span
        className={`absolute inset-0 rounded-full transition-transform duration-300 ${ringColor} ${pulse ? "scale-[3.5]" : "scale-100"}`}
      />
      <span className={`absolute inset-0 rounded-full ${color}`} />
    </span>
  );
}
